import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';

export default function OwnerAudit() {
  const [items, setItems] = useState(null);
  const [practices, setPractices] = useState([]);
  const [pid, setPid] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    api.get('/owner/practices').then((r) => setPractices(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (pid !== 'all') params.practice_id = pid;
    api.get('/owner/audit-logs', { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const filtered = (items || []).filter((l) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (l.action || '').toLowerCase().includes(s) || (l.actor_name || '').toLowerCase().includes(s) || (l.practice_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4" data-testid="owner-audit-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform audit log</h1>
        <p className="text-sm text-muted-foreground">Every management and security-relevant action, across all practices and owner activity.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search action, actor or practice…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="owner-audit-search" />
        </div>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-full sm:w-60" data-testid="owner-audit-practice-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All practices</SelectItem>
            {practices.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!items ? (
        <Skeleton className="h-96" />
      ) : (
        <Card>
          <CardContent className="p-0 divide-y max-h-[65vh] overflow-y-auto" data-testid="owner-audit-list">
            {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No matching log entries.</p>}
            {filtered.map((l, i) => (
              <div key={i} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-32">{(l.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
                <Badge variant="outline" className="text-[10px] capitalize">{l.actor_role || '?'}</Badge>
                <span className="font-medium">{l.actor_name || '—'}</span>
                <span className="text-muted-foreground">{String(l.action || '').replaceAll('_', ' ')}</span>
                <Badge variant="secondary" className="text-[10px] ml-auto">{l.practice_name || 'Platform'}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
